export const EFFECT_PRESET_VERSION = 1
export const EFFECT_PRESET_SOURCE_GLOBAL = 'global'
export const EFFECT_PRESET_SOURCE_USER = 'user'

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const compactObject = (value = {}) =>
  Object.entries(value || {}).reduce((acc, [key, next]) => {
    if (typeof next !== 'undefined') acc[key] = next
    return acc
  }, {})

const normalizeEffectPresetPayload = (preset = {}) => {
  const cloned = cloneJson(preset) || {}
  delete cloned.object
  delete cloned.uuid
  return cloned
}

export const effectPresetValue = (source, id) => `${source}:${id}`

export const parseEffectPresetValue = (value) => {
  const raw = `${value || ''}`.trim()
  const match = raw.match(/^(global|user):(.+)$/)
  if (!match) return { source: '', id: '' }
  return {
    source: match[1],
    id: match[2],
  }
}

export const normalizeEffectPreset = (preset = {}) => {
  if (!preset || typeof preset !== 'object') return null
  const group = `${preset.group || ''}`.trim()
  const code = `${preset.code || preset.id || preset.uuid || ''}`.trim()
  const name = `${preset.name || ''}`.trim()
  if (!group || !code || !name || !preset.preset || typeof preset.preset !== 'object') {
    return null
  }
  return compactObject({
    ...preset,
    version: Number.isFinite(Number(preset.version))
      ? Number(preset.version)
      : EFFECT_PRESET_VERSION,
    id: preset.id || code,
    code,
    name,
    group,
    type: 'effect-preset',
    source: preset.source || EFFECT_PRESET_SOURCE_USER,
    preset: normalizeEffectPresetPayload(preset.preset),
    intentTags: Array.isArray(preset.intentTags) ? preset.intentTags.filter(Boolean) : undefined,
    description: typeof preset.description === 'string' ? preset.description : undefined,
  })
}

export const createEffectUserPreset = ({
  id,
  name,
  group,
  img = '',
  createdAt = Date.now(),
  preset,
} = {}) =>
  normalizeEffectPreset({
    id,
    code: id,
    name,
    group,
    img,
    source: EFFECT_PRESET_SOURCE_USER,
    createdAt,
    preset,
  })

export const createEffectGlobalPresetExport = ({
  id,
  name,
  group,
  img = '',
  description = '',
  intentTags = [],
  preset,
} = {}) =>
  normalizeEffectPreset({
    id,
    code: id,
    name,
    group,
    img,
    source: EFFECT_PRESET_SOURCE_GLOBAL,
    description,
    intentTags,
    preset,
  })

export const effectPresetCatalogFromGlobals = (globalPresets = []) =>
  (globalPresets || []).reduce((acc, preset) => {
    const normalized = normalizeEffectPreset({
      ...preset,
      source: EFFECT_PRESET_SOURCE_GLOBAL,
    })
    if (!normalized) return acc
    if (!acc[normalized.group]) {
      acc[normalized.group] = {
        presets: {},
      }
    }
    acc[normalized.group].presets[normalized.code] = normalized
    return acc
  }, {})

export const buildEffectCatalog = (base = {}, globalPresets = [], stored = null) => {
  const globalCatalog = effectPresetCatalogFromGlobals(globalPresets)
  const source = stored && typeof stored === 'object' ? stored : {}
  const out = { ...base }

  Object.keys(globalCatalog).forEach((group) => {
    const bg = out[group] && typeof out[group] === 'object' ? out[group] : {}
    const gg = globalCatalog[group]
    out[group] = {
      ...bg,
      ...gg,
      presets: {
        ...(bg.presets || {}),
        ...(gg.presets || {}),
      },
    }
  })

  Object.keys(source).forEach((group) => {
    const sg = source[group]
    if (!sg || typeof sg !== 'object') return
    const bg = out[group] && typeof out[group] === 'object' ? out[group] : {}
    out[group] = {
      ...bg,
      ...sg,
      presets: {
        ...(bg.presets || {}),
        ...(sg.presets || {}),
      },
    }
  })

  Object.keys(out).forEach((group) => {
    if (!out[group] || typeof out[group] !== 'object') return
    if (!out[group].presets || typeof out[group].presets !== 'object') {
      out[group].presets = {}
    }
  })

  return out
}

export const serializeUserEffectCatalog = (effects = {}) =>
  Object.keys(effects || {}).reduce((acc, group) => {
    const source = effects[group]
    if (!source || typeof source !== 'object') return acc
    const presets = Object.values(source.presets || {}).reduce((presetAcc, preset) => {
      const normalized = normalizeEffectPreset(preset)
      if (!normalized || normalized.source === EFFECT_PRESET_SOURCE_GLOBAL) return presetAcc
      presetAcc[normalized.code] = normalized
      return presetAcc
    }, {})
    acc[group] = {
      ...source,
      presets,
    }
    return acc
  }, {})
